import React from 'react';
import { CalendarEvent } from '../../../types/calendar';
import { Order } from '../../../types/order';
import { getEventsForDate, sortEventsByTime, formatDateForDisplay } from '../../../utils/calendarUtils';
import { getDaysSinceOrder, getOrderRowBackgroundColor } from '../../../utils/orderUtils';
import EventCard from './EventCard';

interface DayViewProps {
  currentDate: string;
  events: CalendarEvent[];
  orders: Order[];
  onEventClick: (event: CalendarEvent) => void;
}

const DayView: React.FC<DayViewProps> = ({ currentDate, events, orders, onEventClick }) => {
  const allDayEvents = sortEventsByTime(getEventsForDate(events, currentDate));

  // Sort events: incomplete first, completed last (for deliveries)
  const dayEvents = allDayEvents.sort((a, b) => {
    const aCompleted = a.completed ? 1 : 0;
    const bCompleted = b.completed ? 1 : 0;
    return aCompleted - bCompleted;
  });

  const dayOrders = orders.filter(order => order.orderDate === currentDate);

  const allDayEventsFiltered = dayEvents.filter(event => event.allDay);
  const timedEvents = dayEvents.filter(event => !event.allDay);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.dateTitle}>{formatDateForDisplay(currentDate)}</h3>
      </div>

      <div style={styles.content}>
        {/* Orders Section */}
        {dayOrders.length > 0 && (
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Orders ({dayOrders.length})</h4>
            <div style={styles.ordersContainer}>
              {dayOrders.map(order => {
                const daysSince = getDaysSinceOrder(order.orderDate);
                return (
                  <div
                    key={order.id}
                    style={{
                      ...styles.orderCard,
                      backgroundColor: getOrderRowBackgroundColor(daysSince, order.status)
                    }}
                  >
                    <div style={styles.orderHeader}>
                      <span style={styles.orderCustomer}>{order.customerName}</span>
                      <span
                        style={{
                          ...styles.orderStatus,
                          color:
                            order.status === 'Completed'
                              ? '#4CAF50'
                              : order.status === 'In Progress'
                                ? '#FF9800'
                                : '#2196F3'
                        }}
                      >
                        {order.status}
                      </span>
                    </div>
                    {order.email && <div style={styles.orderDetail}>Email: {order.email}</div>}
                    {order.phone && <div style={styles.orderDetail}>Phone: {order.phone}</div>}
                    <div style={styles.orderDetail}>Items: {order.items.length}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All-Day Events Section */}
        {allDayEventsFiltered.length > 0 && (
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>All-Day Events</h4>
            <div style={styles.eventsContainer}>
              {allDayEventsFiltered.map(event => (
                <EventCard key={event.id} event={event} onClick={onEventClick} />
              ))}
            </div>
          </div>
        )}

        {/* Timed Events Section */}
        {timedEvents.length > 0 && (
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Schedule</h4>
            <div style={styles.eventsContainer}>
              {timedEvents.map(event => (
                <EventCard key={event.id} event={event} onClick={onEventClick} />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {dayEvents.length === 0 && dayOrders.length === 0 && (
          <div style={styles.emptyState}>
            <p>No events or orders scheduled for this day</p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  header: {
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fafafa'
  },
  dateTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  },
  content: {
    flex: 1,
    padding: '20px',
    overflow: 'auto'
  },
  section: {
    marginBottom: '30px'
  },
  sectionTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  ordersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  orderCard: {
    padding: '16px',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '8px'
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  orderCustomer: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  orderStatus: {
    fontSize: '14px',
    fontWeight: '600',
    padding: '4px 12px',
    backgroundColor: 'white',
    borderRadius: '12px'
  },
  orderDetail: {
    fontSize: '14px',
    color: '#666',
    marginTop: '4px'
  },
  eventsContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#999',
    fontSize: '16px'
  }
};

export default DayView;
